import { useState, useRef, useCallback, useEffect, type KeyboardEvent, type ChangeEvent, type ClipboardEvent } from 'react'
import { PhotoPreview } from './PhotoPreview'

interface Props {
  onSend: (content: string) => void
  onAddPhoto: (file: File) => void
  onRemovePhoto: (index: number) => void
  photoPreviews: string[]
  disabled?: boolean
}

export function ChatInput({ onSend, onAddPhoto, onRemovePhoto, photoPreviews, disabled }: Props) {
  const [text, setText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const hasPhotos = photoPreviews.length > 0

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim()
    if (!trimmed && !hasPhotos) return
    onSend(trimmed || 'Rozpoznaj roślinę na zdjęciu')
    setText('')
    textareaRef.current?.focus()
  }, [text, hasPhotos, onSend])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) onAddPhoto(file)
        break
      }
    }
  }, [onAddPhoto])

  // Auto-resize textarea to max 4 lines
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.overflow = 'hidden'
    const lineHeight = 20
    const maxHeight = lineHeight * 4
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
    if (textarea.scrollHeight > maxHeight) {
      textarea.style.overflow = 'auto'
    }
  }, [text])

  useEffect(() => {
    if (!disabled) {
      textareaRef.current?.focus()
    }
  }, [disabled])

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      for (const file of files) onAddPhoto(file)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [onAddPhoto])

  return (
    <div className="border-t border-desk shrink-0 px-md py-sm">
      {hasPhotos && (
        <PhotoPreview previews={photoPreviews} onRemove={onRemovePhoto} />
      )}
      <div className="flex items-end gap-xs">
        {/* Agrafka (paperclip) icon */}
        <label
          className="flex h-[2.4rem] w-[2.4rem] shrink-0 cursor-pointer items-center justify-center rounded-full text-frame transition-colors hover:text-botanical"
          title="Załącz zdjęcie"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
          </svg>
        </label>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Napisz do Andrzeja..."
          rows={1}
          disabled={disabled}
          className="flex-1 resize-none rounded-[1.2rem] px-[0.8rem] py-[0.5rem] font-body text-[0.85rem] text-ink outline-none overflow-hidden bg-paper-dark border border-frame"
          onFocus={(e) => { e.target.style.borderColor = 'var(--botanical)' }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--frame)' }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || (!text.trim() && !hasPhotos)}
          className="flex h-[2.4rem] w-[2.4rem] shrink-0 items-center justify-center rounded-full bg-botanical text-paper border-none cursor-pointer transition-[filter] hover:brightness-110 active:brightness-90 disabled:opacity-40"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6"/>
          </svg>
        </button>
      </div>
    </div>
  )
}
